import { useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useNavigate, useParams } from 'react-router-dom'
import {
  addToItems,
  changeQty,
  db,
  findByBarcode,
  mergeOrders,
  moveOrder,
  openOrderForTable,
  saveItems,
  voidOrder,
  type OrderItem,
  type Product,
  type TableRef,
} from '../lib/db'
import { useBarcodeScanner } from '../lib/scanner'
import { elapsedText, money, qtyToText, QTY_UNIT } from '../lib/money'
import { useApp } from '../lib/store'
import { useNow } from '../lib/hooks'
import { AppBar } from '../components/AppBar'
import { WeightSheet } from '../components/WeightSheet'
import { TablePicker, type PickMode } from '../components/TablePicker'
import { Sheet } from '../components/Sheet'
import { NoteSheet } from '../components/NoteSheet'

export function OrderScreen() {
  const { orderId = '' } = useParams()
  const nav = useNavigate()
  const say = useApp((s) => s.say)
  const now = useNow(30_000)

  const [catId, setCatId] = useState<string | null>(null)
  const [cartOpen, setCartOpen] = useState(false)
  const [weighing, setWeighing] = useState<Product | null>(null)
  const [menu, setMenu] = useState(false)
  const [picking, setPicking] = useState<PickMode | null>(null)
  const [noting, setNoting] = useState<OrderItem | null>(null)

  const order = useLiveQuery(() => db.orders.get(orderId), [orderId])
  const categories = useLiveQuery(() => db.categories.orderBy('sortOrder').toArray(), [], [])
  const products = useLiveQuery(() => db.products.orderBy('sortOrder').toArray(), [], [])

  const activeCat = catId ?? categories[0]?.id ?? null
  const shown = useMemo(
    () => products.filter((p) => p.categoryId === activeCat),
    [products, activeCat],
  )

  const items = order?.items ?? []
  const total = items.reduce((s, i) => s + i.lineTotal, 0)
  const count = items.reduce((s, i) => s + i.qtyMilli, 0) / QTY_UNIT

  const qtyOf = (productId: string) =>
    items.filter((i) => i.productId === productId).reduce((s, i) => s + i.qtyMilli, 0)

  async function add(p: Product, option: string | null) {
    if (!order) return
    if (p.soldByWeight) {
      setWeighing(p)
      return
    }
    await saveItems(order.id, addToItems(items, p, option))
  }

  // Máy scan quét mã là thêm thẳng vào đơn, không cần bấm gì. Đang mở bảng cân thì
  // tắt đi — lúc đó người dùng đang gõ số ký, không phải đang quét hàng.
  useBarcodeScanner({
    enabled: !weighing && !menu && !picking && !noting,
    onScan: async (code) => {
      const found = await findByBarcode(code)
      if (!found) {
        say(`Chưa gán mã ${code} cho món nào`)
        return
      }
      // Nhảy sang đúng nhóm của món vừa quét để người bán thấy nó sáng lên.
      if (found.categoryId) setCatId(found.categoryId)
      await add(found, null)
      say(`+ ${found.name}`)
    },
  })

  async function bump(item: OrderItem, delta: number) {
    if (!order) return
    const next = changeQty(items, item.id, delta * QTY_UNIT)
    await saveItems(order.id, next)
    if (next.length === 0) setCartOpen(false)
  }

  async function cancelOrder() {
    if (!order) return
    await voidOrder(order.id)
    say(`Đã huỷ ${order.tableName ?? order.code}`)
    nav('/tables')
  }

  /** Khách đổi chỗ giữa chừng — dời cả đơn sang bàn trống, giờ ngồi giữ nguyên. */
  async function doMove(to: TableRef) {
    if (!order) return
    const from = order.tableName
    await moveOrder(order.id, to)
    setPicking(null)
    say(`Đã chuyển ${from ?? 'đơn'} sang ${to.name}`)
  }

  /** Hai nhóm ghép bàn — dồn món sang bàn kia rồi đóng đơn bên này. */
  async function doMerge(to: TableRef, targetOrderId: string | undefined) {
    if (!order) return

    // Bàn đích luôn phải có đơn đang mở (TablePicker chỉ hiện bàn có khách),
    // nhưng vẫn mở phòng khi đơn vừa được thu tiền ở máy khác.
    const intoId = targetOrderId ?? (await openOrderForTable(to)).id

    await mergeOrders(order.id, intoId)
    setPicking(null)
    say(`Đã gộp vào ${to.name}`)
    nav(`/order/${intoId}`)
  }

  if (!order) return <div className="screen" />

  return (
    <div className="screen">
      <AppBar
        title={order.tableName ?? 'Bán nhanh'}
        sub={
          items.length > 0
            ? `${order.code} · ngồi ${elapsedText(order.openedAt, now)}`
            : `${order.code} · chưa gọi món`
        }
        onBack={() => nav('/tables')}
        right={
          <button type="button" className="icon-btn" onClick={() => setMenu(true)} aria-label="Thêm">
            ⋯
          </button>
        }
      />

      <div className="tabs" role="tablist" aria-label="Nhóm món">
        {categories.map((c) => (
          <button
            key={c.id}
            type="button"
            role="tab"
            aria-selected={c.id === activeCat}
            onClick={() => setCatId(c.id)}
          >
            {c.name}
          </button>
        ))}
      </div>

      <div className="body">
        <div className="product-grid">
          {shown.map((p) => {
            const q = qtyOf(p.id)
            return (
              <div key={p.id} className="product">
                {q > 0 && <span className="product-badge num">{qtyToText(q)}</span>}
                <button type="button" className="product-main" onClick={() => void add(p, null)}>
                  <span className="product-name">{p.name}</span>
                  <span className="product-price num">{money(p.price)}</span>
                </button>

                {p.quickOptions.length > 0 && (
                  <div className="product-opts">
                    {p.quickOptions.map((o) => (
                      <button key={o} type="button" onClick={() => void add(p, o)}>
                        {o}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      <div className={`cart${cartOpen ? ' is-open' : ''}`}>
        <div className="cart-lines">
          {items.map((i) => (
            <div key={i.id} className="cart-line">
              {/* Bấm vào tên món là mở ghi chú: ít đường, nhiều đá, không hành. */}
              <button type="button" className="cart-line-name line-note" onClick={() => setNoting(i)}>
                {i.name}
                {i.optionLabel && <em> · {i.optionLabel}</em>}
                <small className="num">{money(i.unitPrice)}</small>
                {i.note ? (
                  <small className="line-note-text">{i.note}</small>
                ) : (
                  <small className="line-note-add">+ ghi chú</small>
                )}
              </button>
              <div className="stepper">
                <button type="button" onClick={() => void bump(i, -1)} aria-label={`Bớt ${i.name}`}>
                  −
                </button>
                <span className="num">{qtyToText(i.qtyMilli)}</span>
                <button type="button" onClick={() => void bump(i, 1)} aria-label={`Thêm ${i.name}`}>
                  +
                </button>
              </div>
              <div className="cart-line-total num">{money(i.lineTotal)}</div>
            </div>
          ))}
        </div>

        <div className="cart-bar">
          <button
            type="button"
            className="cart-sum"
            onClick={() => setCartOpen((v) => !v)}
            disabled={items.length === 0}
          >
            <span className="cart-count">
              {items.length === 0
                ? 'Chưa gọi món nào'
                : `${qtyToText(count * QTY_UNIT)} món · bấm để sửa`}
            </span>
            <span className="cart-total num">{money(total)}</span>
          </button>
          <button
            type="button"
            className="btn btn-brand btn-lg"
            disabled={items.length === 0}
            onClick={() => nav(`/pay/${order.id}`)}
          >
            Thu tiền
          </button>
        </div>
      </div>

      {menu && (
        <Sheet title={order.tableName ?? 'Bán nhanh'} onClose={() => setMenu(false)}>
          <button
            type="button"
            className="row-btn"
            disabled={!order.tableId}
            onClick={() => {
              setMenu(false)
              setPicking('move')
            }}
          >
            <span className="row-icon" aria-hidden="true">
              ↔️
            </span>
            <span className="row-text">
              <b>Chuyển bàn</b>
              <small>Khách đổi chỗ, dời cả đơn sang bàn trống</small>
            </span>
            <span aria-hidden="true">›</span>
          </button>

          <button
            type="button"
            className="row-btn"
            disabled={items.length === 0}
            onClick={() => {
              setMenu(false)
              setPicking('merge')
            }}
          >
            <span className="row-icon" aria-hidden="true">
              ⤵️
            </span>
            <span className="row-text">
              <b>Gộp bàn</b>
              <small>Dồn món của bàn này sang bàn khác</small>
            </span>
            <span aria-hidden="true">›</span>
          </button>

          {items.length > 0 && (
            <button
              type="button"
              className="btn btn-danger-ghost wide"
              onClick={() => {
                setMenu(false)
                void cancelOrder()
              }}
            >
              Huỷ đơn này
            </button>
          )}
        </Sheet>
      )}

      {picking && (
        <TablePicker
          mode={picking}
          fromTableId={order.tableId}
          onClose={() => setPicking(null)}
          onPick={(table, targetOrder) =>
            picking === 'move' ? doMove(table) : doMerge(table, targetOrder?.id)
          }
        />
      )}

      {noting && (
        <NoteSheet
          item={noting}
          onClose={() => setNoting(null)}
          onSave={async (note) => {
            await saveItems(
              order.id,
              items.map((x) => (x.id === noting.id ? { ...x, note } : x)),
            )
          }}
        />
      )}

      {weighing && (
        <WeightSheet
          product={weighing}
          onClose={() => setWeighing(null)}
          onConfirm={async (qtyMilli) => {
            const next: OrderItem[] = [
              ...items,
              {
                id: crypto.randomUUID(),
                productId: weighing.id,
                name: weighing.name,
                unitPrice: weighing.price,
                qtyMilli,
                lineTotal: Math.round((weighing.price * qtyMilli) / QTY_UNIT),
                optionLabel: null,
                note: null,
              },
            ]
            await saveItems(order.id, next)
            setWeighing(null)
          }}
        />
      )}
    </div>
  )
}
